import { ApiError } from "@/api/client";

/**
 * The four ways a read can fail, as distinct claims about the world
 * (flowstack#923): "you can't see this", "there is nothing here",
 * "the server broke", and "we couldn't ask" must never collapse into
 * one generic screen.
 *
 * ``unknown`` is the deliberate fifth: something unexpected happened
 * and guessing which of the four it was would be worse than saying so.
 */
export type ApiErrorKind = "network" | "forbidden" | "not-found" | "server" | "unknown";

/**
 * Classify a thrown value from a TanStack query into one of the
 * {@link ApiErrorKind}s.
 *
 * Extracted from `AnalysisTab.describeReportError` (#923) so the
 * dashboard's panels classify failures the same way the Analysis
 * surface does rather than growing a second, subtly-different copy.
 * Only the CLASSIFICATION is shared — each surface still writes its
 * own user-facing copy, because "this run could not be found" and
 * "that project could not be found" are different sentences about
 * different things.
 *
 * The network case is narrowed to `TypeError` specifically (Rupert
 * PR-926 r0), not "any error that isn't an `ApiError`": the Fetch
 * spec guarantees a true network failure — offline, CORS, a dropped
 * connection, a request that never got a response at all — rejects
 * `fetch()`'s promise with a `TypeError`, in every browser. A
 * response that WAS received but failed to parse (malformed JSON)
 * throws `SyntaxError`; calling that "could not reach the server"
 * would blame the user's connection for a real server-side bug, so
 * it falls through to `unknown`.
 */
export function classifyApiError(error: unknown): ApiErrorKind {
  if (error instanceof TypeError) return "network";
  if (error instanceof ApiError) {
    if (error.status === 403) return "forbidden";
    if (error.status === 404) return "not-found";
    if (error.status >= 500) return "server";
  }
  return "unknown";
}

/** Message text for an unknown thrown value, without assuming `Error`. */
export function apiErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Unknown error.";
}
